import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, noop } from 'rxjs';
import { MemberRole } from '../../api-generated/models/memberRole';
import { SpinoffPlatformOrgContractService } from '../../api-generated/spinoff-platform-orgs';
import { SpinoffOrgsStore } from '../../orgs/spinoff-orgs.service';
import { ROLE_LABEL, extractErrorMessage, toMemberRow, type MemberRow } from './member-row.util';

/**
 * 팀(반) 하나의 멤버 목록 + role(Admin/Member) 전환 액션 — `org-detail.component.ts`의
 * "팀(반)" 섹션이 team마다 하나씩 이 컴포넌트를 인스턴스화한다.
 *
 * `canManage`는 부모(`OrgDetailComponent`)가 계산해서 넘긴다(org admin 여부 + 팀별 owner
 * 여부는 `OrgSummaryDto.teams`에서 이미 알고 있음) — 이 컴포넌트 자신은 "내가 이 팀을 관리할
 * 수 있는가"를 판단하지 않는다(서버가 최종 인가를 다시 하므로, 여기서 틀려도 403으로만
 * 이어질 뿐 보안 구멍은 아니지만, 판단 로직을 두 곳에 두지 않기 위해).
 *
 * Owner 부여/회수는 Admin↔Member 전환과 별도 액션이다(`promoteToOwner`/`demoteOwner`) — 오너십
 * 이동은 더 무거운 의미라 `confirm()`으로 한 번 더 확인받는다(팀 삭제 등 이 워크스페이스의 다른
 * 되돌리기 어려운 액션과 동일 패턴, `locations-tree.component.ts` 참고).
 *
 * **여러 명이 동시에 team owner일 수 있다(의도된 설계)** — 새 owner를 지정해도 기존 owner를
 * 자동으로 강등하지 않는다. 백엔드(`OrgMembersService.updateMember`의 `{ from, to }` 입력)가 patch 대상
 * 멤버 한 명만 건드리고 팀 안의 다른 멤버는 전혀 조회·수정하지 않으며, `evaluateOrgAccess`도
 * role=Owner인 자리가 여럿이어도 전부 통과시키도록 이미 그렇게 설계돼 있다(2026-08-18 결정,
 * `2026-08-18-org-team-owner-role.md` 노트 참고) — 이 UI가 그 구조를 그대로 노출할 뿐, 새
 * 불변식을 추가하지 않는다.
 */
@Component({
  selector: 'app-team-members-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatCardModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './team-members-panel.component.html',
  styleUrl: './team-members-panel.component.scss',
})
export class TeamMembersPanelComponent {
  private readonly api = inject(SpinoffPlatformOrgContractService);
  private readonly orgsStore = inject(SpinoffOrgsStore);

  readonly orgId = input.required<string>();
  readonly teamId = input.required<string>();
  readonly canManage = input<boolean>(false);

  readonly roleLabel = ROLE_LABEL;
  readonly members = signal<MemberRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** 지금 진행 중인 role 변경 요청 — 어느 memberId의 어느 액션(관리자 전환 vs owner 지정/회수)
   * 인지 하나로 묶는다(행마다 버튼이 최대 2개라 memberId만으로는 어느 버튼의 스피너를 켤지
   * 구분이 안 된다). 요청이 진행 중인 동안은 이 행 저 행 할 것 없이 모든 버튼을 비활성화한다
   * (전환 도중 다른 행을 눌러도 반응이 없는 게 혼란스럽다는 코드 리뷰 지적, 2026-08-18 —
   * team-members-panel.component.html 참고). */
  readonly pending = signal<{ memberId: string; action: 'role' | 'owner' } | null>(null);

  /** orgId/teamId가 바뀔 때마다 새 refresh()가 시작되는데, 먼저 나간 요청이 나중에 응답오면
   * 이미 낡은 컨텍스트의 결과로 members/loading을 덮어쓸 수 있다 — 요청 세대 번호로 "지금
   * 가장 최근에 시작한 요청인가"를 확인한 뒤에만 반영한다(코드 리뷰 지적, 2026-08-18). 이
   * 컴포넌트는 지금 `@for` 항목마다 하나씩 만들어져 orgId/teamId가 보통 안 바뀌지만, 방어적으로
   * 처리해 둔다.
   */
  private requestGeneration = 0;

  protected nextRoleLabel(row: MemberRow): string {
    return row.role === MemberRole.Admin ? '관리자 해제' : '관리자로 지정';
  }

  /** 이 행의 이 액션(스피너)이 지금 진행 중인지 — 템플릿이 버튼별로 묻는다. */
  protected isPending(row: MemberRow, action: 'role' | 'owner'): boolean {
    const p = this.pending();
    return p?.memberId === row.id && p.action === action;
  }

  private confirmName(row: MemberRow): string {
    return row.name ?? row.email ?? row.phone ?? row.userId ?? '이 멤버';
  }

  constructor() {
    effect(() => {
      this.orgId();
      this.teamId();
      void this.refresh();
    });
  }

  async refresh(): Promise<void> {
    const generation = ++this.requestGeneration;
    const orgId = this.orgId();
    const teamId = this.teamId();
    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await firstValueFrom(this.api.spinoffOrgTeamsControllerListTeamMembers(orgId, teamId));
      if (generation !== this.requestGeneration) return;
      this.members.set(res.items.map(toMemberRow));
    } catch {
      if (generation !== this.requestGeneration) return;
      this.error.set('팀 멤버를 불러오지 못했습니다.');
    } finally {
      if (generation === this.requestGeneration) {
        this.loading.set(false);
      }
    }
  }

  async toggleRole(row: MemberRow): Promise<void> {
    if (row.role === MemberRole.Owner || this.pending()) return;
    const nextRole = row.role === MemberRole.Admin ? MemberRole.Member : MemberRole.Admin;
    await this.updateRole(row, nextRole, 'role');
  }

  /** 이 팀의 owner로 지정 — 기존 owner는 건드리지 않는다(공존, 클래스 doc 참고). */
  async promoteToOwner(row: MemberRow): Promise<void> {
    if (row.role === MemberRole.Owner || this.pending()) return;
    if (!confirm(`${this.confirmName(row)}님을 이 팀의 owner로 지정할까요?`)) return;
    await this.updateRole(row, MemberRole.Owner, 'owner');
  }

  /** owner 회수 — Member가 아니라 Admin으로 낮춘다(팀 관리 권한 자체는 유지, 급격한 전락 방지). */
  async demoteOwner(row: MemberRow): Promise<void> {
    if (row.role !== MemberRole.Owner || this.pending()) return;
    if (!confirm(`${this.confirmName(row)}님의 owner 권한을 회수할까요? (관리자로 전환됩니다)`)) return;
    await this.updateRole(row, MemberRole.Admin, 'owner');
  }

  private async updateRole(
    row: MemberRow,
    nextRole: MemberRole,
    action: 'role' | 'owner',
  ): Promise<void> {
    this.pending.set({ memberId: row.id, action });
    this.error.set(null);
    try {
      await firstValueFrom(
        this.api.spinoffOrgTeamsControllerUpdateTeamMemberRole(this.orgId(), this.teamId(), row.id, {
          role: nextRole,
        }),
      );
      await this.refresh();
      // `SpinoffOrgsStore`(org-detail.component.ts의 팀 목록 가시성 판단이 읽는 곳)는 이
      // 변경을 모르는 채로 남는다 — 예를 들어 org admin이 뷰어 본인의 team role을 바꾼
      // 직후에도 스토어 스냅샷은 낡은 채로 남아 "팀(반)" 섹션이 stale해질 수 있다(코드 리뷰
      // 지적, 2026-08-18). invalidate() + refresh()로 다음 조회가 반드시 새로 받아오게 하되,
      // 이 화면의 핵심 흐름(role 전환 성공)과는 무관한 부수효과라 실패해도 무시한다.
      this.orgsStore.invalidate();
      void this.orgsStore.refresh().catch(noop);
    } catch (err) {
      this.error.set(extractErrorMessage(err, '역할 변경에 실패했습니다.'));
    } finally {
      this.pending.set(null);
    }
  }
}
